
import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

export const useScrollAnimation = (options: UseScrollAnimationOptions = {}) => {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (triggerOnce) observer.unobserve(element);
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
};

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  variant?: 'fade-up' | 'fade' | 'slide-left' | 'slide-right';
  staggerChildren?: boolean;
}

const fromMap = {
  'fade-up':    { translateY: 40 },
  'fade':       { translateY: 0 },
  'slide-left': { translateX: -50 },
  'slide-right':{ translateX: 50 },
};

export const AnimatedSection = ({
  children,
  className = '',
  delay = 0,
  variant = 'fade-up',
  staggerChildren = false,
}: AnimatedSectionProps) => {
  const root = useRef<HTMLDivElement>(null);
  const scope = useRef<any>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(el);

        import('animejs').then(({ animate, stagger, createScope }) => {
          scope.current = createScope({ root: el }).add(() => {
            if (staggerChildren) {
              const kids = el.querySelectorAll('.anime-child');
              if (kids.length > 0) {
                animate(kids, {
                  opacity: [0, 1],
                  translateY: [30, 0],
                  duration: 650,
                  ease: 'outExpo',
                  delay: stagger(90, { start: delay }),
                });
                animate(el, { opacity: [0, 1], duration: 1, delay: 0 });
                return;
              }
            }

            const from = fromMap[variant] as Record<string, number>;
            animate(el, {
              opacity: [0, 1],
              ...(variant === 'slide-left' || variant === 'slide-right'
                ? { translateX: [from.translateX, 0] }
                : { translateY: [from.translateY, 0] }),
              duration: 720,
              ease: 'outExpo',
              delay,
            });
          });
        });
      },
      { threshold: 0.08 }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      scope.current?.revert();
    };
  }, [delay, variant, staggerChildren]);

  return (
    <div ref={root} className={className} style={{ opacity: 0 }}>
      {children}
    </div>
  );
};
